import { memo } from "react";
import { Link } from "react-router-dom";
import { ROUTES } from "@routing/routes";
import type { ProductCardData } from "@models/product";
import { getProductImage } from "@utils/productImage";
import { formatPiecePricing, OUT_OF_STOCK } from "@utils/formatter";
import { ProductImage } from "./ProductImage";

interface ProductCardProps {
    product: ProductCardData;
    /**
     * When true, the card image is loaded eagerly with high fetch priority.
     * Use for above-the-fold rows (e.g. the first carousel) to improve LCP.
     */
    priority?: boolean;
}

const ProductCardComponent = ({ product, priority = false }: ProductCardProps) => {
    const imageSrc = getProductImage(product);
    const pricing = formatPiecePricing(
        product.price,
        product.piecesPerUnit,
        product.pricePerPiece,
    );
    // Nothing to buy and nothing to price it by: no live vendor offer (PO ruling
    // 2026-11-02 — the rails carry the flag too), or any product priced 0 that
    // isn't available, whatever its flag says — e.g. one whose variants were all
    // removed (§3.1, BR-04). "$0.00" would read as free. Absent isAvailable (the
    // rails' payloads) reads as unavailable, as it does everywhere else. An
    // out-of-stock product with a real price and offers keeps its price.
    const isUnpriced =
        product.hasOffers === false ||
        (product.price === 0 && product.isAvailable !== true);

    return (
        <Link
            to={ROUTES.PRODUCT_DETAIL(product.id)}
            className="card bg-base-100 shadow-sm hover:shadow-md transition-shadow"
        >
            <figure className="aspect-square overflow-hidden bg-base-200">
                <ProductImage
                    src={imageSrc}
                    alt={product.name}
                    priority={priority}
                />
            </figure>
            <div className="card-body p-3">
                <h3 className="text-sm font-medium text-neutral line-clamp-2">
                    {product.name}
                </h3>
                {isUnpriced ? (
                    <p className="text-sm font-semibold text-neutral/70">
                        {OUT_OF_STOCK}
                    </p>
                ) : (
                    <>
                        <p className="text-sm font-semibold text-neutral">
                            <span>{pricing.unitPrice}</span>
                            <span className="font-normal text-neutral/70"> / unit</span>
                            {pricing.showPiece && (
                                <>
                                    <span className="text-neutral/40"> / </span>
                                    <span>{pricing.piecePrice}</span>
                                    <span className="font-normal text-neutral/70">
                                        {" "}
                                        / piece
                                    </span>
                                </>
                            )}
                        </p>
                        {pricing.showPiece && (
                            <p className="text-xs text-neutral/70">
                                {pricing.piecesPerUnit} pieces per unit
                            </p>
                        )}
                    </>
                )}
            </div>
        </Link>
    );
};

export const ProductCard = memo(ProductCardComponent);
